import { useState, useEffect, useCallback } from "react";
import MarketCountdown from "@/components/MarketCountdown";
import { getEntryOrders, getLimitOrders, getPortfolio, removeFromPortfolio, addToPortfolio, updatePortfolioItem, getTradeHistory, deleteTrade, searchStocks } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogClose
} from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Plus, Trash2, TrendingUp, TrendingDown, PieChart, DollarSign, Percent, Search, Pencil, Clock, ArrowRightLeft, RotateCcw, History, ChevronDown, ChevronRight
} from "lucide-react";
import { toast } from "sonner";
import { SellDialog } from "@/components/SellDialog";
import { LimitOrderDialog } from "@/components/LimitOrderDialog";
import { PnlBreakdownPopover } from "@/components/PnlBreakdownPopover";
import { ShortHint } from "@/components/ShortHint";
import { MiniStopChart } from "@/components/MiniStopChart";
import { EntryOrderForm } from "@/components/EntryOrderForm";
import { PendingEntryOrders } from "@/components/PendingEntryOrders";

function TradeRow({ t, openRebuy, handleDeleteTrade }) {
  const pnlPos = (t.pnl || 0) >= 0;
  return (
    <TableRow className="border-bt-border-subtle hover:bg-bt-hover transition-colors" data-testid={`trade-row-${t.symbol}`}>
      <TableCell>
        <span className="font-mono text-sm font-medium text-bt-text-primary">{t.symbol}</span>
        {t.order_type && <span className={`ml-1.5 px-1 py-0.5 text-[9px] font-mono uppercase border ${t.order_type === "take_profit" ? "bg-bt-positive/10 text-bt-positive border-bt-positive/30" : "bg-bt-negative/10 text-bt-negative border-bt-negative/30"}`} data-testid={`trade-order-${t.id?.slice(0,4)}`}>{{ take_profit: "Take-profit", stop_loss: "Stop-loss", trailing_stop: "Trailing stop" }[t.order_type]}</span>}
        {t.partial && <span className="ml-1.5 px-1 py-0.5 text-[9px] font-mono uppercase bg-bt-gold/15 text-bt-gold border border-bt-gold/30" data-testid={`trade-partial-${t.id?.slice(0,4)}`}>Partial</span>}
        <span className="block text-xs text-bt-text-tertiary">{t.name}</span>
      </TableCell>
      <TableCell>
        <span className={`inline-flex px-2 py-0.5 text-xs font-mono font-medium ${
          t.position_type === "short" ? "bg-bt-negative/15 text-bt-negative border border-bt-negative/30" : "bg-bt-positive/15 text-bt-positive border border-bt-positive/30"
        }`}>{(t.position_type || "long").toUpperCase()}</span>
      </TableCell>
      <TableCell className="text-right font-mono text-sm text-bt-text-primary">{t.shares}</TableCell>
      <TableCell className="text-right font-mono text-sm text-bt-text-secondary">${t.entry_price?.toLocaleString()}</TableCell>
      <TableCell className="text-right font-mono text-sm text-bt-text-primary">${t.exit_price?.toLocaleString()}</TableCell>
      <TableCell className="text-right">
        <span className={`font-mono text-sm ${pnlPos ? "text-bt-positive" : "text-bt-negative"}`}>
          {pnlPos ? "+" : "-"}${Math.abs(t.pnl || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ({pnlPos ? "+" : ""}{t.pnl_pct?.toFixed(2)}%)
        </span>
      </TableCell>
      <TableCell className="text-right font-mono text-xs text-bt-text-tertiary">
        {t.closed_at ? new Date(t.closed_at).toLocaleDateString() : "—"}
      </TableCell>
      <TableCell className="text-right">
        <div className="flex items-center justify-end gap-1">
          <button onClick={() => openRebuy(t)} className="whitespace-nowrap px-2 py-1 text-[10px] font-mono font-medium bg-bt-positive/15 text-bt-positive border border-bt-positive/30 hover:bg-bt-positive/25 transition-colors" data-testid={`rebuy-${t.symbol}-${t.id?.slice(0,4)}`}>RE-BUY</button>
          <button onClick={() => handleDeleteTrade(t.id, t.symbol)} className="text-bt-text-tertiary hover:text-bt-negative transition-colors p-1" data-testid={`delete-trade-${t.id?.slice(0,4)}`}><Trash2 className="w-3.5 h-3.5" /></button>
        </div>
      </TableCell>
    </TableRow>
  );
}

function TradeTableHeader() {
  return (
    <TableHeader>
      <TableRow className="border-bt-border-subtle hover:bg-transparent">
        <TableHead className="text-bt-text-tertiary font-mono text-xs uppercase tracking-wider">Symbol</TableHead>
        <TableHead className="text-bt-text-tertiary font-mono text-xs uppercase tracking-wider">Type</TableHead>
        <TableHead className="text-bt-text-tertiary font-mono text-xs uppercase tracking-wider text-right">Shares</TableHead>
        <TableHead className="text-bt-text-tertiary font-mono text-xs uppercase tracking-wider text-right">Entry</TableHead>
        <TableHead className="text-bt-text-tertiary font-mono text-xs uppercase tracking-wider text-right">Exit</TableHead>
        <TableHead className="text-bt-text-tertiary font-mono text-xs uppercase tracking-wider text-right">P&L</TableHead>
        <TableHead className="text-bt-text-tertiary font-mono text-xs uppercase tracking-wider text-right">Closed</TableHead>
        <TableHead className="text-bt-text-tertiary font-mono text-xs uppercase tracking-wider text-right w-20"></TableHead>
      </TableRow>
    </TableHeader>
  );
}

function TradeTable({ trades, openRebuy, handleDeleteTrade }) {
  return (
    <Table>
      <TradeTableHeader />
      <TableBody>
        {trades.map(t => <TradeRow key={t.id} t={t} openRebuy={openRebuy} handleDeleteTrade={handleDeleteTrade} />)}
      </TableBody>
    </Table>
  );
}

function GroupedTradeView({ trades, groupBy, openRebuy, handleDeleteTrade }) {
  const groups = {};
  trades.forEach(t => {
    let key;
    if (groupBy === "date") {
      key = t.closed_at ? new Date(t.closed_at).toLocaleDateString() : "Unknown";
    } else {
      key = t.symbol || "Unknown";
    }
    if (!groups[key]) groups[key] = [];
    groups[key].push(t);
  });

  return (
    <div>
      {Object.entries(groups).map(([key, groupTrades]) => {
        const groupPnl = groupTrades.reduce((s, t) => s + (t.pnl || 0), 0);
        const groupPnlPos = groupPnl >= 0;
        return (
          <div key={key} className="border-b border-bt-border-subtle last:border-0">
            <div className="flex items-center justify-between px-4 py-2 bg-bt-surface-2">
              <div className="flex items-center gap-3">
                <span className="font-mono text-sm font-medium text-bt-text-primary">{key}</span>
                <span className="text-xs font-mono text-bt-text-tertiary">{groupTrades.length} trade{groupTrades.length > 1 ? "s" : ""}</span>
              </div>
              <span className={`font-mono text-sm font-medium ${groupPnlPos ? "text-bt-positive" : "text-bt-negative"}`}>
                {groupPnlPos ? "+" : "-"}${Math.abs(groupPnl).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            </div>
            <div className="overflow-x-auto">
            <Table>
              <TradeTableHeader />
              <TableBody>
                {groupTrades.map(t => <TradeRow key={t.id} t={t} openRebuy={openRebuy} handleDeleteTrade={handleDeleteTrade} />)}
              </TableBody>
            </Table>
            </div>
          </div>
        );
      })}
    </div>
  );
}

const money = (v, signed = false) => `${Math.round(v || 0) < 0 ? "-" : signed && Math.round(v || 0) > 0 ? "+" : ""}$${Math.abs(v || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

const SummaryTile = ({ label, value, hint, tone, gold, sub, testid, wide }) => (
  <div className={`bg-bt-surface border border-bt-border-subtle px-3 py-3 cursor-help ${wide ? "col-span-2 sm:col-span-1" : ""}`} title={hint} data-testid={testid}>
    <p className="text-[10px] font-mono uppercase tracking-wider text-bt-text-tertiary mb-1">{label} <span className="opacity-60">ⓘ</span></p>
    <p className={`text-lg sm:text-xl font-mono font-medium tracking-tighter ${tone === undefined || Math.round(tone * 10) === 0 ? "text-bt-text-primary" : tone < 0 ? "text-bt-negative" : gold ? "text-bt-gold" : "text-bt-positive"}`}>{value}</p>
    {sub && <p className="text-[10px] font-mono text-bt-text-tertiary mt-0.5 truncate">{sub}</p>}
  </div>
);

export default function PortfolioView() {
  const [portfolio, setPortfolio] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [trades, setTrades] = useState([]);

  // Add form state
  const [searchQ, setSearchQ] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [selectedStock, setSelectedStock] = useState(null);
  const [shares, setShares] = useState("");
  const [buyPrice, setBuyPrice] = useState("");
  const [addMode, setAddMode] = useState("now");
  const [entryOrders, setEntryOrders] = useState([]);
  const [positionType, setPositionType] = useState("long");
  const [acquiredDate, setAcquiredDate] = useState("");

  // Edit state
  const [editItem, setEditItem] = useState(null);
  const [editShares, setEditShares] = useState("");
  const [editPrice, setEditPrice] = useState("");
  const [editType, setEditType] = useState("long");
  const [editDate, setEditDate] = useState("");
  const [editOpen, setEditOpen] = useState(false);

  // Re-buy state
  const [rebuyTrade, setRebuyTrade] = useState(null);
  const [rebuyShares, setRebuyShares] = useState("");
  const [rebuyOpen, setRebuyOpen] = useState(false);
  const [sellItem, setSellItem] = useState(null);
  const [orderItem, setOrderItem] = useState(null);
  const [orders, setOrders] = useState([]);

  // Trade history state
  const [historyOpen, setHistoryOpen] = useState(true);
  const [groupBy, setGroupBy] = useState("all"); // all, date, symbol

  const fetchPortfolio = useCallback(async () => {
    try {
      const [pRes, tRes, oRes] = await Promise.all([
        getPortfolio(),
        getTradeHistory().catch(() => ({ data: [] })),
        getLimitOrders().catch(() => ({ data: [] })),
        getEntryOrders().then(r => setEntryOrders(r.data || [])).catch(() => {}),
      ]);
      setPortfolio(pRes.data || []);
      setTrades(tRes.data || []);
      setOrders(oRes.data || []);
      setLastUpdated(new Date());
    } catch {
      toast.error("Failed to load portfolio");
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    window.addEventListener("orders-changed", fetchPortfolio);
    return () => window.removeEventListener("orders-changed", fetchPortfolio);
  }, [fetchPortfolio]);

  useEffect(() => {
    fetchPortfolio();
    const interval = setInterval(fetchPortfolio, 60000);
    return () => clearInterval(interval);
  }, [fetchPortfolio]);

  const handleSearch = useCallback(async (q) => {
    if (q.length < 1) { setSearchResults([]); return; }
    try {
      const res = await searchStocks(q);
      setSearchResults(res.data || []);
    } catch { setSearchResults([]); }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => handleSearch(searchQ), 300);
    return () => clearTimeout(t);
  }, [searchQ, handleSearch]);

  const handleSelectStock = (stock) => {
    setSelectedStock(stock);
    setBuyPrice(stock.price?.toString() || "");
    setSearchQ("");
    setSearchResults([]);
  };

  const handleAdd = async () => {
    if (!selectedStock || !shares || parseFloat(shares) <= 0 || !buyPrice || parseFloat(buyPrice) <= 0) {
      toast.error("Fill in all fields");
      return;
    }
    try {
      await addToPortfolio({
        symbol: selectedStock.symbol,
        name: selectedStock.name,
        shares: parseFloat(shares),
        buy_price: parseFloat(buyPrice),
        asset_type: selectedStock.asset_type || "stock",
        position_type: positionType,
        acquired_date: acquiredDate
      });
      toast.success(`Added ${positionType.toUpperCase()} ${selectedStock.symbol} to portfolio`);
      setDialogOpen(false);
      setSelectedStock(null);
      setShares("");
      setBuyPrice("");
      setPositionType("long");
      setAcquiredDate("");
      fetchPortfolio();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add");
    }
  };

  const handleRemove = async (id, symbol) => {
    try {
      await removeFromPortfolio(id);
      setPortfolio(prev => prev.filter(p => p.id !== id));
      toast.success(`${symbol} removed from portfolio`);
    } catch {
      toast.error("Failed to remove");
    }
  };

  const openRebuy = (trade) => {
    setRebuyTrade(trade);
    setRebuyShares(trade.shares?.toString() || "");
    setRebuyOpen(true);
  };

  const handleRebuy = async () => {
    if (!rebuyTrade || !rebuyShares || parseFloat(rebuyShares) <= 0) {
      toast.error("Enter valid number of shares"); return;
    }
    try {
      await addToPortfolio({
        symbol: rebuyTrade.symbol,
        name: rebuyTrade.name,
        shares: parseFloat(rebuyShares),
        buy_price: rebuyTrade.exit_price,
        asset_type: rebuyTrade.asset_type || "stock",
        position_type: rebuyTrade.position_type || "long",
        acquired_date: new Date().toISOString().slice(0, 10)
      });
      toast.success(`Re-bought ${rebuyShares} ${rebuyTrade.symbol} at $${rebuyTrade.exit_price.toLocaleString()}`);
      setRebuyOpen(false);
      setRebuyTrade(null);
      setRebuyShares("");
      fetchPortfolio();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to re-buy");
    }
  };

  const handleDeleteTrade = async (id, symbol) => {
    try {
      await deleteTrade(id);
      setTrades(prev => prev.filter(t => t.id !== id));
      toast.success(`${symbol} trade deleted`);
    } catch {
      toast.error("Failed to delete trade");
    }
  };

  const openEdit = (item) => {
    setEditItem(item);
    setEditShares(item.shares?.toString() || "");
    setEditPrice(item.buy_price?.toString() || "");
    setEditType(item.position_type || "long");
    setEditDate(item.acquired_date || "");
    setEditOpen(true);
  };

  const handleEdit = async () => {
    if (!editItem || !editShares || parseFloat(editShares) <= 0 || !editPrice || parseFloat(editPrice) <= 0) {
      toast.error("Enter valid values"); return;
    }
    try {
      await updatePortfolioItem(editItem.id, {
        shares: parseFloat(editShares),
        buy_price: parseFloat(editPrice),
        position_type: editType,
        acquired_date: editDate,
      });
      toast.success(`${editItem.symbol} updated`);
      setEditOpen(false);
      setEditItem(null);
      fetchPortfolio();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update");
    }
  };

  const totalValue = portfolio.reduce((s, p) => s + (p.total_value || 0), 0);
  const totalInvested = portfolio.reduce((s, p) => s + (p.buy_price || 0) * (p.shares || 0), 0);
  const totalPnl = portfolio.reduce((s, p) => s + (p.pnl || 0), 0);
  const totalPnlPct = totalInvested > 0 ? (totalPnl / totalInvested * 100) : 0;

  // Realized P&L from closed trades
  const realizedPnl = trades.reduce((s, t) => s + (t.pnl || 0), 0);
  const winningTrades = trades.filter(t => (t.pnl || 0) > 0);
  const losingTrades = trades.filter(t => (t.pnl || 0) < 0);
  const totalWins = winningTrades.reduce((s, t) => s + t.pnl, 0);
  const totalLosses = losingTrades.reduce((s, t) => s + t.pnl, 0);
  const winRate = trades.length > 0 ? ((winningTrades.length / trades.length) * 100) : 0;
  const combinedPnl = totalPnl + realizedPnl;

  return (
    <div className="max-w-[1600px] mx-auto px-3 sm:px-4 md:px-8 py-4 sm:py-6" data-testid="portfolio-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-8 gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-semibold tracking-tight text-bt-text-primary">
            My Picks
          </h1>
          <p className="text-base text-bt-text-secondary font-body mt-2">
            Stocks and crypto you've picked yourself, with your own limit orders
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:gap-4">
          <MarketCountdown />
          {lastUpdated && (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-bt-surface border border-bt-border-subtle" data-testid="portfolio-last-updated">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-bt-positive opacity-50" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-bt-positive" />
              </span>
              <Clock className="w-3 h-3 text-bt-text-tertiary" strokeWidth={1.5} />
              <span className="font-mono text-xs text-bt-text-secondary">{lastUpdated.toLocaleTimeString()}</span>
            </div>
          )}
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-bt-gold text-bt-bg hover:bg-bt-gold-light font-body" data-testid="add-holding-btn">
              <Plus className="w-4 h-4 mr-2" /> Add Holding
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-bt-surface border-bt-border-subtle text-bt-text-primary sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading text-lg">Add to My Picks</DialogTitle>
              <DialogDescription className="text-sm text-bt-text-secondary">Search for a stock and add it to your portfolio.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 pt-2">
              {!selectedStock ? (
                <div>
                  <Label className="text-bt-text-secondary font-mono text-xs">Search Stock</Label>
                  <div className="relative mt-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-bt-text-tertiary" />
                    <Input
                      value={searchQ}
                      onChange={(e) => setSearchQ(e.target.value)}
                      placeholder="AAPL, MSFT..."
                      className="pl-10 bg-bt-surface-2 border-bt-border-subtle text-bt-text-primary font-mono"
                      data-testid="portfolio-search-input"
                    />
                  </div>
                  {searchResults.length > 0 && (
                    <div className="mt-2 border border-bt-border-subtle bg-bt-surface-2 max-h-48 overflow-y-auto">
                      {searchResults.map(r => (
                        <button
                          key={r.symbol}
                          onClick={() => handleSelectStock(r)}
                          className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-bt-hover transition-colors text-left"
                          data-testid={`portfolio-search-result-${r.symbol}`}
                        >
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-sm text-bt-text-primary">{r.symbol}</span>
                            {r.asset_type === "crypto" && <span className="text-[10px] font-mono px-1.5 py-0.5 bg-bt-gold/15 text-bt-gold border border-bt-gold/30">CRYPTO</span>}
                          </div>
                          <span className="text-xs text-bt-text-secondary">{r.name}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between bg-bt-surface-2 border border-bt-border-subtle px-4 py-3">
                    <div>
                      <span className="font-mono text-sm font-medium text-bt-text-primary">{selectedStock.symbol}</span>
                      <span className="ml-2 text-xs text-bt-text-secondary">{selectedStock.name}</span>
                    </div>
                    <button onClick={() => setSelectedStock(null)} className="text-xs text-bt-text-tertiary hover:text-bt-gold font-mono">
                      Change
                    </button>
                  </div>
                  {/* Position Type Toggle */}
                  <div>
                    <Label className="text-bt-text-secondary font-mono text-xs">Position Type</Label>
                    <div className="flex gap-2 mt-1">
                      <button
                        onClick={() => setPositionType("long")}
                        className={`flex-1 py-2 text-sm font-mono font-medium transition-colors ${
                          positionType === "long"
                            ? "bg-bt-positive/20 text-bt-positive border border-bt-positive/40"
                            : "bg-bt-surface-2 text-bt-text-secondary border border-bt-border-subtle hover:border-bt-border-focus"
                        }`}
                        data-testid="portfolio-position-long-btn"
                      >
                        Long
                      </button>
                      <button
                        onClick={() => setPositionType("short")}
                        className={`flex-1 py-2 text-sm font-mono font-medium transition-colors ${
                          positionType === "short"
                            ? "bg-bt-negative/20 text-bt-negative border border-bt-negative/40"
                            : "bg-bt-surface-2 text-bt-text-secondary border border-bt-border-subtle hover:border-bt-border-focus"
                        }`}
                        data-testid="portfolio-position-short-btn"
                      >
                        Short
                      </button>
                    </div>
                  </div>
                  <ShortHint active={positionType === "short"} testid="short-hint-add" />
                  <div className="grid grid-cols-2 gap-1 p-1 bg-bt-surface-2 border border-bt-border-subtle" data-testid="add-mode-toggle">
                    {[["now", positionType === "short" ? "Short now" : "Buy now"], ["level", "At a price level"]].map(([m, l]) => (
                      <button key={m} type="button" onClick={() => setAddMode(m)} data-testid={`add-mode-${m}`}
                        className={`py-1.5 text-xs font-mono transition-colors ${addMode === m ? "bg-bt-surface text-bt-text-primary shadow-sm font-semibold" : "text-bt-text-tertiary hover:text-bt-text-primary"}`}>{l}</button>
                    ))}
                  </div>
                  {addMode === "level" ? (
                    <EntryOrderForm key={`${selectedStock.symbol}-${positionType}`} stock={selectedStock} positionType={positionType}
                      livePrice={selectedStock.price} onDone={() => { setDialogOpen(false); setSelectedStock(null); setAddMode("now"); fetchPortfolio(); }} />
                  ) : (<>
                  <div>
                    <Label className="text-bt-text-secondary font-mono text-xs">{positionType === "short" ? "Short Price ($)" : "Buy Price ($)"}</Label>
                    <Input
                      type="number"
                      min="0.01"
                      step="0.01"
                      value={buyPrice}
                      onChange={(e) => setBuyPrice(e.target.value)}
                      className="mt-1 bg-bt-surface-2 border-bt-border-subtle text-bt-text-primary font-mono"
                      data-testid="portfolio-buy-price-input"
                    />
                  </div>
                  <div>
                    <Label className="text-bt-text-secondary font-mono text-xs">Number of Shares</Label>
                    <Input
                      type="number"
                      min="0.01"
                      step="0.01"
                      value={shares}
                      onChange={(e) => setShares(e.target.value)}
                      placeholder="e.g., 10"
                      className="mt-1 bg-bt-surface-2 border-bt-border-subtle text-bt-text-primary font-mono"
                      data-testid="portfolio-shares-input"
                    />
                  </div>
                  <div>
                    <Label className="text-bt-text-secondary font-mono text-xs">Acquisition Date</Label>
                    <Input
                      type="date"
                      value={acquiredDate}
                      onChange={(e) => setAcquiredDate(e.target.value)}
                      className="mt-1 bg-bt-surface-2 border-bt-border-subtle text-bt-text-primary font-mono"
                      data-testid="portfolio-acquired-date-input"
                    />
                  </div>
                  {shares && buyPrice && parseFloat(shares) > 0 && parseFloat(buyPrice) > 0 && (
                    <p className="font-mono text-sm text-bt-text-secondary">
                      Total Investment: ${(parseFloat(buyPrice) * parseFloat(shares)).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </p>
                  )}
                  <Button
                    onClick={handleAdd}
                    className="w-full bg-bt-gold text-bt-bg hover:bg-bt-gold-light font-body"
                    data-testid="portfolio-confirm-add-btn"
                  >
                    Add to My Picks
                  </Button>
                  </>)}
                </>
              )}
            </div>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Summary Cards - compact single row */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-6">
        <SummaryTile label="Value" testid="portfolio-total-value-card" value={money(totalValue)}
          hint="What your open positions are worth right now. A short counts as its collateral minus any loss (or plus any gain)." />
        <SummaryTile label="Invested" testid="portfolio-invested-card" value={money(totalInvested)}
          hint="Entry price × shares for every open position — the money you put in." />
        <SummaryTile label="Open P&L" testid="portfolio-pnl-card" value={money(totalPnl, true)} tone={totalPnl}
          hint="Unrealized: gain or loss on positions you still hold. Value − Invested. Not locked in until you sell." />
        <SummaryTile label="Open Return" testid="portfolio-return-card" value={`${totalPnlPct >= 0 ? "+" : ""}${totalPnlPct.toFixed(1)}%`} tone={totalPnlPct}
          hint="Open P&L ÷ Invested." />
        <PnlBreakdownPopover portfolio={portfolio} trades={trades} realized={realizedPnl} open={totalPnl}>
          <button type="button" className="col-span-2 sm:col-span-1 text-left hover:ring-1 hover:ring-bt-gold/40 transition-shadow" data-testid="portfolio-total-pnl-trigger">
            <SummaryTile label="Total P&L" testid="portfolio-total-pnl-card" value={money(combinedPnl, true)} tone={combinedPnl} gold
              sub={`Realized ${money(realizedPnl, true)} + Open ${money(totalPnl, true)}`}
              hint="Tap to see the per-symbol breakdown. Everything combined: profit already banked from closed trades (Realized) plus Open P&L." />
          </button>
        </PnlBreakdownPopover>
      </div>

      <PendingEntryOrders orders={entryOrders} livePrices={Object.fromEntries(portfolio.map(p => [p.symbol, p.current_price]))} onChanged={fetchPortfolio} />
      <ShortHint active={portfolio.some(p => p.position_type === "short")} testid="short-hint-holdings" className="mb-3" />

      {/* Holdings Table */}
      <div className="bg-bt-surface border border-bt-border-subtle" data-testid="holdings-table">
        {loading ? (
          <div className="p-6 space-y-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-12 w-full bg-bt-surface-2" />
            ))}
          </div>
        ) : portfolio.length === 0 ? (
          <div className="p-12 text-center" data-testid="portfolio-empty">
            <PieChart className="w-12 h-12 text-bt-text-tertiary mx-auto mb-4" strokeWidth={1} />
            <p className="font-heading text-xl text-bt-text-primary mb-2">No holdings yet</p>
            <p className="text-sm text-bt-text-secondary font-body mb-6">
              Start building your portfolio by adding stocks or cryptocurrencies.
            </p>
            <Button onClick={() => setDialogOpen(true)} className="bg-bt-gold text-bt-bg hover:bg-bt-gold-light font-body" data-testid="empty-add-holding-btn">
              <Plus className="w-4 h-4 mr-2" /> Add Your First Holding
            </Button>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="border-bt-border-subtle hover:bg-transparent">
                <TableHead className="text-bt-text-tertiary font-mono text-xs uppercase tracking-wider">Symbol</TableHead>
                <TableHead className="text-bt-text-tertiary font-mono text-xs uppercase tracking-wider">Date</TableHead>
                <TableHead className="text-bt-text-tertiary font-mono text-xs uppercase tracking-wider text-right">Shares</TableHead>
                <TableHead className="text-bt-text-tertiary font-mono text-xs uppercase tracking-wider">Type</TableHead>
                <TableHead className="text-bt-text-tertiary font-mono text-xs uppercase tracking-wider text-right">Entry</TableHead>
                <TableHead className="text-bt-text-tertiary font-mono text-xs uppercase tracking-wider text-right">Current</TableHead>
                <TableHead className="text-bt-text-tertiary font-mono text-xs uppercase tracking-wider text-right" title="30-day price vs your entry (grey) and stop/target lines">30D vs Stop</TableHead>
                <TableHead className="text-bt-text-tertiary font-mono text-xs uppercase tracking-wider text-right">Net Value</TableHead>
                <TableHead className="text-bt-text-tertiary font-mono text-xs uppercase tracking-wider text-right">P&L</TableHead>
                <TableHead className="text-bt-text-tertiary font-mono text-xs uppercase tracking-wider text-right w-16"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {portfolio.map(p => {
                const pnlPositive = (p.pnl || 0) >= 0;
                return (
                  <TableRow key={p.id} className="border-bt-border-subtle hover:bg-bt-hover transition-colors" data-testid={`holding-row-${p.symbol}`}>
                    <TableCell>
                      <span className="font-mono text-sm font-medium text-bt-text-primary">{p.symbol}</span>
                      <span className="block text-xs text-bt-text-tertiary truncate max-w-[120px]">{p.name}</span>
                      {orders.filter(o => o.portfolio_item_id === p.id).map(o => (
                        <span key={o.id} className={`inline-block mr-1 mt-1 px-1 py-0.5 text-[9px] font-mono border ${o.order_type === "take_profit" ? "text-bt-positive border-bt-positive/30 bg-bt-positive/10" : "text-bt-negative border-bt-negative/30 bg-bt-negative/10"}`} data-testid={`order-badge-${p.symbol}-${o.order_type}`}>
                          {{ take_profit: "TP", stop_loss: "SL", trailing_stop: `TS ${o.trail_pct}%` }[o.order_type]} ${o.target_price}
                        </span>
                      ))}
                    </TableCell>
                    <TableCell className="font-mono text-xs text-bt-text-tertiary">{p.acquired_date || "—"}</TableCell>
                    <TableCell className="text-right font-mono text-sm text-bt-text-primary">{p.shares}</TableCell>
                    <TableCell>
                      <span className={`inline-flex px-2 py-0.5 text-xs font-mono font-medium ${
                        p.position_type === "short"
                          ? "bg-bt-negative/15 text-bt-negative border border-bt-negative/30"
                          : "bg-bt-positive/15 text-bt-positive border border-bt-positive/30"
                      }`} data-testid={`holding-type-${p.symbol}`}>
                        {(p.position_type || "long").toUpperCase()}
                      </span>
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm text-bt-text-secondary">${p.buy_price?.toLocaleString()}</TableCell>
                    <TableCell className="text-right font-mono text-sm text-bt-text-primary">${p.current_price?.toLocaleString()}</TableCell>
                    <TableCell className="text-right py-1"><MiniStopChart item={p} orders={orders.filter(o => o.portfolio_item_id === p.id)} /></TableCell>
                    <TableCell className="text-right font-mono text-sm text-bt-text-primary">
                      ${p.total_value?.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </TableCell>
                    <TableCell className="text-right">
                      <span className={`font-mono text-sm ${pnlPositive ? "text-bt-positive" : "text-bt-negative"}`}>
                        {pnlPositive ? "+" : "-"}${Math.abs(p.pnl || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ({pnlPositive ? "+" : ""}{p.pnl_pct?.toFixed(2)}%)
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => setSellItem(p)}
                          className="px-2 py-1 text-[10px] font-mono font-medium bg-bt-negative/15 text-bt-negative border border-bt-negative/30 hover:bg-bt-negative/25 transition-colors"
                          data-testid={`sell-holding-${p.symbol}`}
                        >
                          SELL
                        </button>
                        <button
                          onClick={() => setOrderItem(p)}
                          className="whitespace-nowrap px-2 py-1 text-[10px] font-mono font-medium bg-bt-gold/10 text-bt-gold border border-bt-gold/30 hover:bg-bt-gold/20 transition-colors"
                          title="Take-profit / stop-loss orders"
                          data-testid={`orders-holding-${p.symbol}`}
                        >
                          ORDERS
                        </button>
                        <button
                          onClick={() => openEdit(p)}
                          className="text-bt-text-tertiary hover:text-bt-gold transition-colors p-1"
                          data-testid={`edit-holding-${p.symbol}`}
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => handleRemove(p.id, p.symbol)}
                          className="text-bt-text-tertiary hover:text-bt-negative transition-colors p-1"
                          data-testid={`remove-holding-${p.symbol}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Trade P&L Summary */}
      {trades.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 mt-6" data-testid="trade-pnl-panel">
          <div className="bg-bt-surface border border-bt-border-subtle px-3 py-3">
            <p className="text-[10px] font-mono uppercase tracking-wider text-bt-text-tertiary mb-1 cursor-help" title="Profit or loss already locked in from positions you've closed.">Realized P&L ⓘ</p>
            <p className={`text-lg font-mono font-medium tracking-tighter ${realizedPnl >= 0 ? "text-bt-positive" : "text-bt-negative"}`} data-testid="realized-pnl">
              {money(realizedPnl, true)}
            </p>
          </div>
          <div className="bg-bt-surface border border-bt-border-subtle px-3 py-3">
            <p className="text-[10px] font-mono uppercase tracking-wider text-bt-text-tertiary mb-1 cursor-help" title="Same as Open P&L: gain or loss on positions you still hold.">Unrealized P&L ⓘ</p>
            <p className={`text-lg font-mono font-medium tracking-tighter ${totalPnl >= 0 ? "text-bt-positive" : "text-bt-negative"}`} data-testid="unrealized-pnl">
              {money(totalPnl, true)}
            </p>
          </div>
          <div className="bg-bt-surface border border-bt-border-subtle px-3 py-3">
            <p className="text-[10px] font-mono uppercase tracking-wider text-bt-text-tertiary mb-1 cursor-help" title="Realized + Unrealized — same as Total P&L above.">Combined P&L ⓘ</p>
            <p className={`text-lg font-mono font-medium tracking-tighter ${combinedPnl >= 0 ? "text-bt-gold" : "text-bt-negative"}`} data-testid="combined-pnl">
              {money(combinedPnl, true)}
            </p>
          </div>
          <div className="bg-bt-surface border border-bt-border-subtle px-3 py-3">
            <p className="text-[10px] font-mono uppercase tracking-wider text-bt-text-tertiary mb-1">Wins / Losses</p>
            <p className="text-lg font-mono font-medium tracking-tighter text-bt-text-primary">
              <span className="text-bt-positive">{winningTrades.length}</span>
              <span className="text-bt-text-tertiary mx-1">/</span>
              <span className="text-bt-negative">{losingTrades.length}</span>
            </p>
          </div>
          <div className="bg-bt-surface border border-bt-border-subtle px-3 py-3">
            <p className="text-[10px] font-mono uppercase tracking-wider text-bt-text-tertiary mb-1">Win Rate</p>
            <p className={`text-lg font-mono font-medium tracking-tighter ${winRate >= 50 ? "text-bt-positive" : "text-bt-negative"}`}>
              {winRate.toFixed(0)}%
            </p>
          </div>
          <div className="bg-bt-surface border border-bt-border-subtle px-3 py-3">
            <p className="text-[10px] font-mono uppercase tracking-wider text-bt-text-tertiary mb-1">Avg Win / Loss</p>
            <p className="text-lg font-mono font-medium tracking-tighter text-bt-text-primary">
              <span className="text-bt-positive text-sm">${winningTrades.length ? (totalWins / winningTrades.length).toLocaleString(undefined, { maximumFractionDigits: 0 }) : "0"}</span>
              <span className="text-bt-text-tertiary mx-1 text-sm">/</span>
              <span className="text-bt-negative text-sm">${losingTrades.length ? Math.abs(totalLosses / losingTrades.length).toLocaleString(undefined, { maximumFractionDigits: 0 }) : "0"}</span>
            </p>
          </div>
        </div>
      )}

      {/* Trade History */}
      {trades.length > 0 && (
        <div className="bg-bt-surface border border-bt-border-subtle mt-6" data-testid="trade-history">
          <button
            onClick={() => setHistoryOpen(!historyOpen)}
            className="w-full flex items-center gap-2 px-4 py-3 border-b border-bt-border-subtle hover:bg-bt-hover transition-colors text-left"
            data-testid="toggle-trade-history"
          >
            {historyOpen ? <ChevronDown className="w-4 h-4 text-bt-text-tertiary" /> : <ChevronRight className="w-4 h-4 text-bt-text-tertiary" />}
            <History className="w-4 h-4 text-bt-gold" strokeWidth={1.5} />
            <span className="text-xs font-mono uppercase tracking-wider text-bt-text-secondary">Trade History</span>
            <span className="text-xs font-mono text-bt-text-tertiary ml-auto">{trades.length} trades</span>
          </button>
          {historyOpen && (
            <>
              {/* Group By Tabs */}
              <div className="flex items-center gap-1 px-4 py-2 border-b border-bt-border-subtle">
                <span className="text-[10px] font-mono uppercase tracking-wider text-bt-text-tertiary mr-2">Group:</span>
                {["all", "date", "symbol"].map(g => (
                  <button
                    key={g}
                    onClick={() => setGroupBy(g)}
                    className={`px-3 py-1 text-xs font-mono transition-colors ${
                      groupBy === g ? "bg-bt-gold text-bt-bg" : "text-bt-text-secondary hover:text-bt-text-primary hover:bg-bt-surface-2"
                    }`}
                    data-testid={`group-by-${g}`}
                  >
                    {g === "all" ? "All" : g === "date" ? "By Date" : "By Symbol"}
                  </button>
                ))}
              </div>
              {groupBy === "all" ? (
                <TradeTable trades={trades} openRebuy={openRebuy} handleDeleteTrade={handleDeleteTrade} />
              ) : (
                <GroupedTradeView trades={trades} groupBy={groupBy} openRebuy={openRebuy} handleDeleteTrade={handleDeleteTrade} />
              )}
            </>
          )}
        </div>
      )}

      {/* Edit Holding Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="bg-bt-surface border-bt-border-subtle text-bt-text-primary sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg">Edit {editItem?.symbol}</DialogTitle>
            <DialogDescription className="text-sm text-bt-text-secondary">Update shares, entry price, or position type.</DialogDescription>
          </DialogHeader>
          {editItem && (
            <div className="space-y-4 pt-2">
              <div className="flex items-center justify-between bg-bt-surface-2 border border-bt-border-subtle px-4 py-3">
                <div>
                  <span className="font-mono text-sm font-medium text-bt-text-primary">{editItem.symbol}</span>
                  <span className="ml-2 text-xs text-bt-text-secondary">{editItem.name}</span>
                </div>
              </div>
              {/* Position Type */}
              <div>
                <Label className="text-bt-text-secondary font-mono text-xs">Position Type</Label>
                <div className="flex gap-2 mt-1">
                  <button
                    onClick={() => setEditType("long")}
                    className={`flex-1 py-2 text-sm font-mono font-medium transition-colors ${
                      editType === "long"
                        ? "bg-bt-positive/20 text-bt-positive border border-bt-positive/40"
                        : "bg-bt-surface-2 text-bt-text-secondary border border-bt-border-subtle hover:border-bt-border-focus"
                    }`}
                    data-testid="edit-position-long-btn"
                  >
                    Long
                  </button>
                  <button
                    onClick={() => setEditType("short")}
                    className={`flex-1 py-2 text-sm font-mono font-medium transition-colors ${
                      editType === "short"
                        ? "bg-bt-negative/20 text-bt-negative border border-bt-negative/40"
                        : "bg-bt-surface-2 text-bt-text-secondary border border-bt-border-subtle hover:border-bt-border-focus"
                    }`}
                    data-testid="edit-position-short-btn"
                  >
                    Short
                  </button>
                </div>
              </div>
              <div>
                <Label className="text-bt-text-secondary font-mono text-xs">{editType === "short" ? "Short Price ($)" : "Entry Price ($)"}</Label>
                <Input
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={editPrice}
                  onChange={(e) => setEditPrice(e.target.value)}
                  className="mt-1 bg-bt-surface-2 border-bt-border-subtle text-bt-text-primary font-mono"
                  data-testid="edit-price-input"
                />
              </div>
              <div>
                <Label className="text-bt-text-secondary font-mono text-xs">Number of Shares</Label>
                <Input
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={editShares}
                  onChange={(e) => setEditShares(e.target.value)}
                  className="mt-1 bg-bt-surface-2 border-bt-border-subtle text-bt-text-primary font-mono"
                  data-testid="edit-shares-input"
                />
              </div>
              <div>
                <Label className="text-bt-text-secondary font-mono text-xs">Acquisition Date</Label>
                <Input
                  type="date"
                  value={editDate}
                  onChange={(e) => setEditDate(e.target.value)}
                  className="mt-1 bg-bt-surface-2 border-bt-border-subtle text-bt-text-primary font-mono"
                  data-testid="edit-acquired-date-input"
                />
              </div>
              <Button
                onClick={handleEdit}
                className="w-full bg-bt-gold text-bt-bg hover:bg-bt-gold-light font-body"
                data-testid="edit-confirm-btn"
              >
                Save Changes
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Re-Buy Dialog */}
      <Dialog open={rebuyOpen} onOpenChange={setRebuyOpen}>
        <DialogContent className="bg-bt-surface border-bt-border-subtle text-bt-text-primary sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg">Re-Buy {rebuyTrade?.symbol}</DialogTitle>
            <DialogDescription className="text-sm text-bt-text-secondary">Re-enter a position at the current market price.</DialogDescription>
          </DialogHeader>
          {rebuyTrade && (
            <div className="space-y-4 pt-2">
              <div className="bg-bt-surface-2 border border-bt-border-subtle px-4 py-3">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <span className="font-mono text-sm font-medium text-bt-text-primary">{rebuyTrade.symbol}</span>
                    <span className="ml-2 text-xs text-bt-text-secondary">{rebuyTrade.name}</span>
                  </div>
                  <span className={`inline-flex px-2 py-0.5 text-xs font-mono font-medium ${
                    rebuyTrade.position_type === "short"
                      ? "bg-bt-negative/15 text-bt-negative border border-bt-negative/30"
                      : "bg-bt-positive/15 text-bt-positive border border-bt-positive/30"
                  }`}>{(rebuyTrade.position_type || "long").toUpperCase()}</span>
                </div>
                <div className="flex justify-between text-xs font-mono text-bt-text-tertiary">
                  <span>Previous exit: ${rebuyTrade.exit_price?.toLocaleString()}</span>
                  <span>Previous shares: {rebuyTrade.shares}</span>
                </div>
              </div>
              <div>
                <Label className="text-bt-text-secondary font-mono text-xs">Current Trading Price</Label>
                <p className="font-mono text-2xl font-medium text-bt-text-primary mt-1" data-testid="rebuy-current-price">
                  ${rebuyTrade.exit_price?.toLocaleString()}
                </p>
              </div>
              <div>
                <Label className="text-bt-text-secondary font-mono text-xs">Number of Shares</Label>
                <Input
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={rebuyShares}
                  onChange={(e) => setRebuyShares(e.target.value)}
                  placeholder="e.g., 100"
                  className="mt-1 bg-bt-surface-2 border-bt-border-subtle text-bt-text-primary font-mono"
                  data-testid="rebuy-shares-input"
                />
              </div>
              {rebuyShares && parseFloat(rebuyShares) > 0 && (
                <div className="bg-bt-surface-2 border border-bt-border-subtle px-4 py-3">
                  <p className="text-xs font-mono uppercase tracking-wider text-bt-text-tertiary mb-1">Total Cost</p>
                  <p className="font-mono text-xl font-medium text-bt-gold" data-testid="rebuy-total-cost">
                    ${(rebuyTrade.exit_price * parseFloat(rebuyShares)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                </div>
              )}
              <Button
                onClick={handleRebuy}
                className="w-full bg-bt-positive text-bt-bg hover:bg-bt-positive/90 font-body font-medium"
                data-testid="rebuy-confirm-btn"
              >
                <RotateCcw className="w-4 h-4 mr-2" /> Re-Buy {rebuyTrade.symbol}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
      <LimitOrderDialog item={orderItem} orders={orders} onClose={() => setOrderItem(null)} onChanged={fetchPortfolio} />
      <SellDialog item={sellItem} onClose={() => setSellItem(null)} onSold={fetchPortfolio} onOrdersChanged={fetchPortfolio} />
    </div>
  );
}
